import { isArray } from 'lodash';
import { StatusProp } from 'scouting-ui/lib/StatusTag/StatusTag';

import { AdvancementStatus } from '@appTypes/enums';

import { AdvancementStatusType, advancementStatuses } from '../constants';
import isValueTruthy from './isValueTruthy';

type AdvancementInfo = Partial<{
  dateCompleted: string | null;
  dateEarned: string | null;
  awardedDate: string | null;
  awardedUserId: number | string | null;
  markedCompletedDate: string | null;
  markedCompletedUserId: number | null;
  leaderApprovedUserId: number | null;
  leaderApprovedDate: string | null;
  counselorApprovedUserId: number | null;
  awarded: boolean | null;
  status: string | null;
  dateStarted: string | null;
  percentCompleted: number | null;
}>;

const isAwarded = ({
  status,
  awarded,
  awardedDate,
  awardedUserId,
}: AdvancementInfo) =>
  status === 'Awarded' ||
  !!(isValueTruthy(awarded) || awardedDate || awardedUserId);

const isApproved = ({
  leaderApprovedDate,
  leaderApprovedUserId,
}: AdvancementInfo) => !!(leaderApprovedDate || leaderApprovedUserId);
const isAtLeastApproved = (item: AdvancementInfo) =>
  isApproved(item) || isAwarded(item);

export function isCounselorApproved({
  counselorApprovedUserId,
  status,
}: AdvancementInfo = {}) {
  switch (status) {
    case advancementStatuses.COUNSELOR_APPROVED:
    case 'Counselor Approved':
    case 'COUNSELOR_APPROVED':
      return true;
  }

  // if not status just rely in counselor approved user id
  if (!status) {
    return !!counselorApprovedUserId;
  }
  // otherwise only mark as Counselor Approved if status is completed
  if (status === AdvancementStatus.Completed && !!counselorApprovedUserId)
    return true;

  return false;
}

const isAtLeastCounselorApproved = (item: AdvancementInfo) =>
  isCounselorApproved(item) || isAtLeastApproved(item);

const isCompleted = ({
  dateCompleted,
  dateEarned,
  markedCompletedDate,
  status,
  markedCompletedUserId,
}: AdvancementInfo) =>
  !!(
    status === 'Completed' ||
    dateCompleted ||
    dateEarned ||
    markedCompletedDate ||
    markedCompletedUserId
  );
const isAtLeastCompleted = (item: AdvancementInfo) =>
  isCompleted(item) || isAtLeastCounselorApproved(item);

const isStarted = ({
  dateStarted,
  status,
  percentCompleted,
}: AdvancementInfo) =>
  !!dateStarted || status == 'Started' || Number(percentCompleted) > 0;

const isAtLeastStarted = (item: AdvancementInfo) =>
  isStarted(item) || isAtLeastCompleted(item);

const advancementStatusValues = Object.freeze({
  [advancementStatuses.AWARDED]: 5,
  [advancementStatuses.COUNSELOR_APPROVED]: 4,
  [advancementStatuses.APPROVED]: 3,
  [advancementStatuses.COMPLETED]: 2,
  [advancementStatuses.STARTED]: 1,
});

const getStatus = (item: AdvancementInfo) => {
  if (isAwarded(item)) {
    return advancementStatuses.AWARDED;
  }
  if (isApproved(item)) {
    return advancementStatuses.APPROVED;
  }
  if (isCounselorApproved(item)) {
    return advancementStatuses.COUNSELOR_APPROVED;
  }
  if (isCompleted(item)) {
    return advancementStatuses.COMPLETED;
  }
  if (isStarted(item)) {
    return advancementStatuses.STARTED;
  }

  return advancementStatuses.NOT_STARTED;
};

export function isAdvancementStatus(
  advancement: {
    status: AdvancementStatus | string;
    counselorApprovedUserId?: number;
  },
  statusToCheck: AdvancementStatus | AdvancementStatus[],
) {
  const statuses = isArray(statusToCheck) ? statusToCheck : [statusToCheck];
  const match = statuses.some(status => advancement?.status === status);

  // Custom logic for counselor approved
  if (
    advancement.status === AdvancementStatus.Completed &&
    advancement.counselorApprovedUserId &&
    statuses.includes(AdvancementStatus.CounselorApproved)
  ) {
    return true;
  }

  return match;
}

export function isStatus(
  status: string,
  /** List of normalized status string. See `advancementStatuses` */
  statusToCheck: AdvancementStatusType[],
) {
  const _getStatus = (status: string) => {
    switch (status) {
      case AdvancementStatus.Awarded:
        return advancementStatuses.AWARDED;
      case AdvancementStatus.Started:
        return advancementStatuses.STARTED;
      case AdvancementStatus.Completed:
        return advancementStatuses.COMPLETED;
      case AdvancementStatus.LeaderApproved:
        return advancementStatuses.APPROVED;
      case AdvancementStatus.NotStarted:
        return advancementStatuses.NOT_STARTED;
      case AdvancementStatus.CounselorApproved:
        return advancementStatuses.COUNSELOR_APPROVED;
      default:
        return status as AdvancementStatusType;
    }
  };

  const parsedStatus = _getStatus(status);

  return statusToCheck.includes(parsedStatus);
}

export function advancementStatusToTagStatus(
  status: string = '',
): StatusProp | string {
  const normalized = status.toLowerCase();
  switch (normalized) {
    case 'started':
      return StatusProp.STARTED;
    case 'completed':
    case 'earned':
      return StatusProp.COMPLETED;
    case 'approved':
    case 'leader approved':
      return StatusProp.APPROVED;
    case 'awarded':
      return StatusProp.AWARDED;
    case 'declined':
      return StatusProp.DECLINED;
    case '':
      return StatusProp.NOT_STARTED;
    default:
      return normalized;
  }
}

const statusCompare = (status1: string, status2: string) =>
  advancementStatusValues[status1 as keyof typeof advancementStatusValues] -
  advancementStatusValues[status2 as keyof typeof advancementStatusValues];

export default {
  statusCompare,
  isStarted,
  isAtLeastStarted,
  isCompleted,
  isAtLeastCompleted,
  isCounselorApproved,
  isAtLeastCounselorApproved,
  isApproved,
  isAtLeastApproved,
  isAwarded,
  getStatus,
};
